import { Component, computed, input } from '@angular/core';

type SvgPathDefinition = {
  d: string;
  transform?: string;
  fillRule?: string;
  clipRule?: string;
  strokeWidth?: string;
  strokeLinecap?: string;
  strokeLinejoin?: string;
  fill?: string;
  stroke?: string;
  fillOpacity?: string;
  strokeOpacity?: string;
};

type SvgIconDefinition = {
  viewBox: string;
  paths: SvgPathDefinition[];
};

// <use> has limited support so instead define icons here
const SVG_ICON_DEFINITIONS: Record<string, SvgIconDefinition> = {
  'arrow-left': {
    viewBox: '0 0 32 32',
    paths: [{
      d: 'm24.308 15-6.025-6.293a1.025 1.025 0 0 1 0-1.414.944.944 0 0 1 1.367 0L28 16l-8.35 8.707a.944.944 0 0 1-1.367 0 1.025 1.025 0 0 1 0-1.414L24.308 17H4.818C4.366 17 4 16.552 4 16s.366-1 .818-1z',
      transform: 'translate(32,0) scale(-1,1)',
      fillRule: 'evenodd',
    }],
  },
  'arrow-right': {
    viewBox: '0 0 32 32',
    paths: [{
      d: 'm24.308 15-6.025-6.293a1.025 1.025 0 0 1 0-1.414.944.944 0 0 1 1.367 0L28 16l-8.35 8.707a.944.944 0 0 1-1.367 0 1.025 1.025 0 0 1 0-1.414L24.308 17H4.818C4.366 17 4 16.552 4 16s.366-1 .818-1z',
      fillRule: 'evenodd',
    }],
  },
  'checkmark': {
    viewBox: '0 0 32 32',
    paths: [{
      d: 'm14.781 21.367-5.074-5.074a1 1 0 1 0-1.414 1.414l6.926 6.926 8.65-15.137a1 1 0 1 0-1.737-.992z',
      fillRule: 'evenodd',
    }],
  },
  'chevron-down-filled': {
    viewBox: '0 0 32 32',
    paths: [{
      d: 'm4 22 12-12 12 12z',
      transform: 'matrix(1 0 0 -1 0 32)',
      fillRule: 'evenodd',
    }],
  },
  'chevron-right': {
    viewBox: '0 0 32 32',
    paths: [{
      d: 'm19.266 16-8.983 9.293a1.025 1.025 0 0 0 0 1.414c.378.39.99.39 1.367 0L22 16 11.65 5.293a.944.944 0 0 0-1.367 0 1.025 1.025 0 0 0 0 1.414z',
      fillRule: 'evenodd',
    }],
  },
  'close': {
    viewBox: '0 0 32 32',
    paths: [{
      d: 'm17.414 16 5.657 5.657a1 1 0 0 1-1.414 1.414L16 17.414l-5.657 5.657a1 1 0 0 1-1.414-1.414L14.586 16l-5.657-5.657a1 1 0 0 1 1.414-1.414L16 14.586l5.657-5.657a1 1 0 0 1 1.414 1.414z',
      fillRule: 'evenodd',
    }],
  },
  'email': {
    viewBox: '0 0 32 32',
    paths: [{
      d: 'M28 6V26H4V6H28ZM26 9.142L16 19.1421L6 9.142V24H26V9.142ZM24.313 8H7.687L16 16.3137L24.313 8Z',
      fillRule: 'evenodd',
      clipRule: 'evenodd',
    }],
  },
  'feedback': {
    viewBox: '0 0 32 32',
    paths: [{
      d: 'm28 27-6.972-2.917C19.498 24.67 17.795 25 16 25 9.373 25 4 20.523 4 15S9.373 5 16 5s12 4.477 12 10c0 2.272-.91 4.367-2.441 6.046zm-3.682-3.709-1.09-2.657.853-.935C25.33 18.33 26 16.7 26 15c0-4.33-4.405-8-10-8S6 10.67 6 15s4.405 8 10 8c1.514 0 2.976-.27 4.309-.784l.75-.288zM10 16h12v2H10zm0-4h12v2H10z',
      fillRule: 'evenodd',
    }],
  },
  'flag': {
    viewBox: '0 0 24 26',
    paths: [{
      d: 'M1 25.0006V17.9166M1 17.9166C8.75733 11.8499 14.576 23.9832 22.3333 17.9166V2.75123C14.576 8.8179 8.75733 -3.31544 1 2.75123V17.9166Z',
      fillRule: 'evenodd',
      strokeWidth: '2',
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
    }],
  },
  'info-hollow': {
    viewBox: '0 0 20 20',
    paths: [{
      d: 'M10 2.5C14.1421 2.5 17.5 5.85786 17.5 10C17.5 14.1421 14.1421 17.5 10 17.5C5.85786 17.5 2.5 14.1421 2.5 10C2.5 5.85786 5.85786 2.5 10 2.5ZM10 3.75C6.54822 3.75 3.75 6.54822 3.75 10C3.75 13.4518 6.54822 16.25 10 16.25C13.4518 16.25 16.25 13.4518 16.25 10C16.25 6.54822 13.4518 3.75 10 3.75ZM10 8.75C10.5178 8.75 10.9375 9.16973 10.9375 9.6875V13.4375C10.9375 13.9553 10.5178 14.375 10 14.375C9.48223 14.375 9.0625 13.9553 9.0625 13.4375V9.6875C9.0625 9.16973 9.48223 8.75 10 8.75ZM10 5.625C10.5178 5.625 10.9375 6.04473 10.9375 6.5625C10.9375 7.08027 10.5178 7.5 10 7.5C9.48223 7.5 9.0625 7.08027 9.0625 6.5625C9.0625 6.04473 9.48223 5.625 10 5.625Z',
      fillRule: 'evenodd',
      clipRule: 'evenodd',
    }],
  },
  'info-solid': {
    viewBox: '0 0 32 32',
    paths: [{
      d: 'M16 4c6.627 0 12 5.373 12 12s-5.373 12-12 12S4 22.627 4 16 9.373 4 16 4m0 10a1.5 1.5 0 0 0-1.493 1.356l-.007.144v6a1.5 1.5 0 0 0 2.993.144l.007-.144v-6A1.5 1.5 0 0 0 16 14m0-5a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3',
      fillRule: 'evenodd',
    }],
  },
  'linkedin': {
    viewBox: '0 0 32 32',
    paths: [{
      d: 'M24.45 24.45h-3.557v-5.57c0-1.328-.024-3.037-1.85-3.037-1.852 0-2.135 1.447-2.135 2.94v5.666h-3.556V12.997h3.414v1.565h.048a3.74 3.74 0 0 1 3.368-1.85c3.604 0 4.269 2.37 4.269 5.455zM9.34 11.43a2.064 2.064 0 1 1-.001-4.127 2.064 2.064 0 0 1 0 4.127zm1.778 13.018h-3.56V12.997h3.56zM26.222 4.002H5.771A1.75 1.75 0 0 0 4 5.732v20.535A1.753 1.753 0 0 0 5.771 28h20.451A1.756 1.756 0 0 0 28 26.267V5.73A1.755 1.755 0 0 0 26.222 4z',
      fillRule: 'evenodd',
    }],
  },
  'pdf': {
    viewBox: '0 0 18 20',
    paths: [{
      d: 'M13.5 4.93945V10.5H17.25C17.6642 10.5 18 10.8358 18 11.25V18.75C18 19.1642 17.6642 19.5 17.25 19.5H0.75C0.335786 19.5 0 19.1642 0 18.75V1.5C0 0.335786 0.335786 0 1.5 0H8.56055L13.5 4.93945ZM1.5 11.9844V17.8496H2.91406V15.8604H3.81641C4.53056 15.8603 5.09245 15.6889 5.50195 15.3467C5.9115 15.0043 6.11621 14.5466 6.11621 13.9746C6.11619 13.5879 6.02134 13.2423 5.83203 12.9375C5.64276 12.6329 5.37613 12.3975 5.03125 12.2324C4.68625 12.0673 4.28916 11.9844 3.84082 11.9844H1.5ZM6.87012 11.9844V17.8496H8.7998C9.3071 17.8468 9.76666 17.7256 10.1787 17.4854C10.5909 17.245 10.9108 16.912 11.1377 16.4863C11.3646 16.0607 11.4785 15.5791 11.4785 15.042V14.7725C11.4758 14.2407 11.3583 13.7604 11.126 13.332C10.8937 12.9037 10.5707 12.5719 10.1572 12.3369C9.74365 12.1019 9.2771 11.9844 8.75879 11.9844H6.87012ZM12.2031 11.9844V17.8496H13.6172V15.5137H15.8975V14.4258H13.6172V13.0762H16.127V11.9844H12.2031ZM8.75879 13.0762C9.17505 13.0762 9.49243 13.2205 9.70996 13.5078C9.92727 13.7951 10.0361 14.2218 10.0361 14.7881V15.042C10.0361 15.6112 9.92737 16.0404 9.70996 16.3291C9.49247 16.6177 9.18079 16.7626 8.77539 16.7627H8.28418V13.0762H8.75879ZM3.85645 13.0762C4.11157 13.0789 4.31323 13.1624 4.46094 13.3262C4.60854 13.4899 4.68255 13.7087 4.68262 13.9824C4.68262 14.2321 4.61037 14.4255 4.4668 14.5625C4.32318 14.6994 4.11457 14.7685 3.84082 14.7686H2.91406V13.0762H3.85645ZM1.5 10.5H12V6H7.5V1.5H1.5V10.5ZM9 4.5H10.9395L9 2.56055V4.5Z',
      fillRule: 'evenodd',
    }],
  },
  'printer': {
    viewBox: '0 0 32 32',
    paths: [{
      d: 'M24 8H25C26.6569 8 28 9.34315 28 11V21C28 22.6569 26.6569 24 25 24H24V28H8V24H7C5.34315 24 4 22.6569 4 21V11C4 9.34315 5.34315 8 7 8H8V4H24V8ZM10 26H22V18H10V26ZM7 10C6.44772 10 6 10.4477 6 11V21C6 21.5523 6.44772 22 7 22H8V16H24V22H25C25.5523 22 26 21.5523 26 21V11C26 10.4477 25.5523 10 25 10H7ZM10 8H22V6H10V8Z',
      fillRule: 'evenodd',
    }],
  },
  'profile': {
    viewBox: '0 0 32 32',
    paths: [{
      d: 'M16 2c7.732 0 14 6.268 14 14s-6.268 14-14 14S2 23.732 2 16 8.268 2 16 2M7.749 23.48a8 8 0 0 0-.534.695A11.97 11.97 0 0 0 16 28c3.469 0 6.593-1.472 8.784-3.825a8 8 0 0 0-.645-.825c-1.154-1.29-2.775-2.294-4.665-2.854L16 23.97l-3.475-3.475c-1.952.58-3.617 1.632-4.776 2.983zm5.395-5.195.484.484 1.278 1.278h-.001L16 21.142l2.857-2.857q.22.045.44.097l-.44-.097a14 14 0 0 1 2.19.64c1.707.66 3.197 1.645 4.355 2.86q.35.367.657.76A11.94 11.94 0 0 0 28 16c0-6.627-5.373-12-12-12S4 9.373 4 16c0 2.415.714 4.664 1.942 6.547a9 9 0 0 1 .627-.73c1.162-1.23 2.663-2.226 4.385-2.893l.102-.038q.13-.05.264-.097l-.366.136a13 13 0 0 1 1.77-.548q.208-.048.42-.092M16 8a5 5 0 1 1 0 10 5 5 0 0 1 0-10m0 2a3 3 0 1 0 0 6 3 3 0 0 0 0-6',
      fillRule: 'evenodd',
    }],
  },
  'save': {
    viewBox: '0 0 24 24',
    paths: [{
      d: 'M23.8 5.2L18.8 0.2C18.6 0.2 18.2 0 18 0H2C0.8 0 0 0.8 0 2V22C0 23.2 0.8 24 2 24H22C23.2 24 24 23.2 24 22V6C24 5.8 23.8 5.4 23.8 5.2ZM8 2H16V6H8V2ZM16 22H8V14H16V22ZM18 22V14C18 12.8 17.2 12 16 12H8C6.8 12 6 12.8 6 14V22H2V2H6V6C6 7.2 6.8 8 8 8H16C17.2 8 18 7.2 18 6V2.4L22 6.4V22H18Z',
      fillRule: 'evenodd',
    }],
  },
  'search': {
    viewBox: '0 0 32 32',
    paths: [{
      d: 'M14 4c5.523 0 10 4.477 10 10 0 2.4-.846 4.604-2.256 6.328l5.963 5.965a1 1 0 0 1-1.414 1.414l-5.965-5.963A9.96 9.96 0 0 1 14 24C8.477 24 4 19.523 4 14S8.477 4 14 4m0 2a8 8 0 1 0 0 16 8 8 0 0 0 0-16',
      fillRule: 'evenodd',
    }],
  },
  'sign-in': {
    viewBox: '0 0 32 32',
    paths: [{
      d: 'M28 4v24H8v-2h18V6H8V4zm-11.35 6.293L22 16l-5.35 5.707a.944.944 0 0 1-1.367 0 1.025 1.025 0 0 1 0-1.414l3.054-3.294L5.091 17C4.488 17 4 16.552 4 16s.488-1 1.09-1l13.247-.001-3.054-3.292a1.025 1.025 0 0 1 0-1.414.944.944 0 0 1 1.367 0',
      fillRule: 'evenodd',
    }],
  },
  'sign-out': {
    viewBox: '0 0 32 32',
    paths: [{
      d: 'M28 4H4v24h24v-2H6V6h22zM11.09 17h13.248l-3.055 3.293a1.025 1.025 0 0 0 0 1.414c.378.39.99.39 1.367 0L28 16l-5.35-5.707a.944.944 0 0 0-1.367 0 1.025 1.025 0 0 0 0 1.414L24.338 15H11.091c-.603 0-1.091.448-1.091 1s.488 1 1.09 1',
      fillRule: 'evenodd',
      clipRule: 'evenodd',
    }],
  },
  'trash': {
    viewBox: '0 0 22 26',
    paths: [{
      d: 'M17.8828 26H4.11719L1.86719 8H20.1328L17.8828 26ZM5.88281 24H7.96777L7.53027 10H4.13281L5.88281 24ZM9.96973 24H12.0303L12.4678 10H9.53223L9.96973 24ZM14.0322 24H16.1172L17.8672 10H14.4697L14.0322 24ZM13 0C13.5523 0 14 0.447715 14 1V4H21C21.5523 4 22 4.44772 22 5C22 5.55228 21.5523 6 21 6H1C0.447715 6 0 5.55228 0 5C0 4.44772 0.447715 4 1 4H8V1C8 0.447715 8.44771 0 9 0H13ZM10 4H12V2H10V4Z',
      fillRule: 'evenodd',
    }],
  },
  'warning-fill': {
    viewBox: '0 0 32 32',
    paths: [{
      d: 'M17.181 4.331c.31.189.571.449.76.759l10.727 19.46a2.267 2.267 0 0 1-1.94 3.45H5.271a2.27 2.27 0 0 1-1.94-3.449L14.058 5.09a2.274 2.274 0 0 1 3.122-.759zM16 21.5a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3m0-11a1.5 1.5 0 0 0-1.5 1.5v6a1.5 1.5 0 0 0 3 0v-6a1.5 1.5 0 0 0-1.5-1.5',
      fillRule: 'evenodd',
    }],
  },
  'x': {
    viewBox: '0 0 24 24',
    paths: [{
      d: 'M13.714 10.623 20.416 3h-1.588l-5.821 6.617L8.36 3H3l7.028 10.007L3 21h1.588l6.144-6.99L15.64 21H21zm-2.175 2.472-.713-.997L5.16 4.171H7.6l4.573 6.4.71.997 5.945 8.318h-2.44l-4.849-6.79Z',
      fillRule: 'evenodd',
    }],
  },
};

/*
* This component is needed to allow for inline svgs with changeable colours
* Firefox has a bug where svgs in <img> tags that are styled using filter functions
* are not printed to pdfs, but instead are blank
*/
@Component({
  selector: 'lib-svg-icon',
  templateUrl: './svg-icon.component.html',
  styleUrls: ['./svg-icon.component.scss'],
})
export class SvgIconComponent {
  public icon = input<string>('');
  public stroke = input<boolean>(false);
  public fill = input<boolean>(false);

  public iconDefinition = computed(() => SVG_ICON_DEFINITIONS[this.icon()]);

  public fillOpacity = computed(() => {
    return this.fill() ? '1' : '0';
  });

  public strokeOpacity = computed(() => {
    return this.stroke() ? '1' : '0';
  });
}
